import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import { config } from '@/config';
import { prepareHeaders } from '@/configuration/tokenHandling/prepareHeaders';
import type { EngineOilFleetPage, EngineOilFleetRequest } from '@/features/engineoil/table/types';
import type {
    Assignment,
    CsvFilters,
    EngineOil,
    EngineOilTableResponse,
    Workshop,
} from '@/generated/GeneratedApiTypes';

// Partial so the transform stays defensive against a malformed/empty body.
type EngineOilFleetPageResponse = Partial<EngineOilTableResponse>;

const getDefaultCsvFilename = () => `Export_EngineOil-${new Date().toISOString().slice(0, 10)}.csv`;
const CONTENT_DISPOSITION_FILENAME_REGEX = /filename="?([^"]+)"?/;

const emptyPage = (request: EngineOilFleetRequest): EngineOilFleetPage => ({
    items: [],
    page: request.page,
    size: request.size,
    totalItems: 0,
    totalPages: 0,
});

export const api = createApi({
    reducerPath: 'api',
    baseQuery: fetchBaseQuery({ baseUrl: config.backend.BACKEND_URI, prepareHeaders }),
    endpoints: (build) => ({
        getEngineOil: build.query<EngineOil | null, string>({
            query: (assetId) => `/engine-oil/readings/${assetId}`,
            transformResponse: (response: EngineOil | '') => response || null,
        }),
        getEngineOilList: build.query<EngineOilFleetPage, EngineOilFleetRequest>({
            query: (body) => ({
                url: '/engine-oil/readings',
                method: 'POST',
                // ui counts page starting on 1 BE on 0
                body: { ...body, page: body.page - 1 },
            }),
            transformResponse: (response: EngineOilFleetPageResponse | '', _meta, request) => {
                if (!response) {
                    return emptyPage(request);
                }
                return {
                    items: response.items ?? [],
                    // ui counts page starting on 1 BE on 0
                    page: response.page === undefined ? request.page : response.page + 1,
                    size: response.size ?? request.size,
                    totalItems: response.total_items ?? 0,
                    totalPages: response.total_pages ?? 0,
                };
            },
        }),
        getAssignment: build.query<Assignment, string>({
            query: (assetId) => `/engine-oil/assignments/${assetId}`,
        }),
        getWorkshop: build.query<Workshop, string>({
            query: (workshopId) => `/engine-oil/workshops/${workshopId}`,
        }),
        exportCsv: build.mutation<null, CsvFilters>({
            queryFn: async (filters, _queryApi, _extraOptions, baseQuery) => {
                const result = await baseQuery({
                    url: '/engine-oil/readings/csv-export',
                    method: 'POST',
                    body: filters,
                    responseHandler: (response) => response.blob(),
                });

                if (result.error) {
                    return { error: result.error };
                }

                const blob = result.data as Blob;
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;

                const contentDisposition = result.meta?.response?.headers.get('Content-Disposition');
                const backendFilename = contentDisposition?.match(CONTENT_DISPOSITION_FILENAME_REGEX)?.[1];
                link.download = backendFilename || getDefaultCsvFilename();

                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);

                return { data: null };
            },
        }),
    }),
});

export const {
    useGetEngineOilQuery,
    useGetEngineOilListQuery,
    useGetAssignmentQuery,
    useGetWorkshopQuery,
    useExportCsvMutation,
} = api;
